import type { FixtureWithRefs, StandingWithTeam } from '@/lib/data/provider';

type LiveFixture = Pick<
  FixtureWithRefs,
  'home_team_id' | 'away_team_id' | 'home_score' | 'away_score' | 'status'
>;

/**
 * A PROVISIONAL standings table that folds a live fixture's current score into
 * the two teams' rows (played +1, points/GD/W-D-L from the in-play result),
 * re-ranks each group, and sets each row's `status` to up/down/same versus its
 * stored rank - so the table shows live movement arrows while the match runs.
 *
 * Display-only and transient: never persisted. Once the fixture finishes, the
 * backend writes the real standings + movement and this is no longer used.
 */
export function computeProvisionalStandings(
  fixture: LiveFixture,
  rows: StandingWithTeam[],
): StandingWithTeam[] {
  if (fixture.status !== 'live') return rows;
  const homeId = fixture.home_team_id;
  const awayId = fixture.away_team_id;
  if (!homeId || !awayId) return rows;
  if (!rows.some((r) => r.team_id === homeId) || !rows.some((r) => r.team_id === awayId)) {
    return rows;
  }
  const homeGoals = fixture.home_score ?? 0;
  const awayGoals = fixture.away_score ?? 0;

  const apply = (row: StandingWithTeam): StandingWithTeam => {
    const isHome = row.team_id === homeId;
    const isAway = row.team_id === awayId;
    if (!isHome && !isAway) return row;
    const gf = isHome ? homeGoals : awayGoals;
    const ga = isHome ? awayGoals : homeGoals;
    const win = gf > ga;
    const draw = gf === ga;
    return {
      ...row,
      played: row.played + 1,
      won: row.won + (win ? 1 : 0),
      drawn: row.drawn + (draw ? 1 : 0),
      lost: row.lost + (!win && !draw ? 1 : 0),
      gd: row.gd + (gf - ga),
      points: row.points + (win ? 3 : draw ? 1 : 0),
    };
  };

  const storedRank = new Map(rows.map((row) => [row.id, row.rank]));
  const adjusted = rows.map(apply);

  // Re-rank WITHIN each group (group-stage tables must not mix groups).
  const byGroup = new Map<string, StandingWithTeam[]>();
  for (const row of adjusted) {
    const key = row.group_label ?? '';
    const bucket = byGroup.get(key);
    if (bucket) bucket.push(row);
    else byGroup.set(key, [row]);
  }

  const result: StandingWithTeam[] = [];
  for (const group of byGroup.values()) {
    group.sort((a, b) => b.points - a.points || b.gd - a.gd || b.won - a.won);
    group.forEach((row, index) => {
      const rank = index + 1;
      const old = storedRank.get(row.id) ?? rank;
      const status = rank < old ? 'up' : rank > old ? 'down' : 'same';
      result.push({ ...row, rank, status });
    });
  }
  return result;
}
